// src/components/Marketing/IDSRoadmapTimeline.tsx
import { motion } from "framer-motion";
import { CheckCircle2, Rocket, Layers, Zap, Brain } from "lucide-react";

export default function IDSRoadmapTimeline() {
    const phases = [
        {
            icon: <Rocket className="h-5 w-5 text-sky-500" />,
            title: "Phase 1 — Foundation",
            period: "2025 Q1–Q3",
            color: "from-sky-500 to-sky-700",
            description:
                "Establish the IDSCRM Core Platform — unified dealer, finance, and customer portals with secure authentication, database, and communication systems.",
            highlights: ["Core API", "Dealer Portal", "Finance App", "Real-Time Chat", "Email/SMTP"],
            status: "complete",
        },
        {
            icon: <Layers className="h-5 w-5 text-emerald-500" />,
            title: "Phase 2 — Expansion",
            period: "2025 Q4–2026 Q2",
            color: "from-emerald-500 to-emerald-700",
            description:
                "Integrate partner systems and expand role-based dashboards across Service, Admin, and Lender portals. Introduce data syncing with DMS and automotive feeds.",
            highlights: ["Service Portal", "Admin Console", "Bank/Lender Portal", "DMS Integrations"],
            status: "active",
        },
        {
            icon: <Zap className="h-5 w-5 text-amber-500" />,
            title: "Phase 3 — Automation",
            period: "2026 Q3–2027",
            color: "from-amber-500 to-amber-700",
            description:
                "Automate workflows using AI-driven insights for pricing, lead scoring, and predictive finance approvals — creating a self-learning dealership network.",
            highlights: ["AI Forecasting", "Lead Scoring", "Predictive Finance", "Smart Pricing"],
            status: "upcoming",
        },
        {
            icon: <Brain className="h-5 w-5 text-purple-500" />,
            title: "Phase 4 — Ecosystem Intelligence",
            period: "Beyond 2027",
            color: "from-purple-500 to-purple-700",
            description:
                "Connect IDSCRM with broader fintech and data networks. Launch Goonie Pay, expand AutoBidders, and integrate blockchain-style dealer verification.",
            highlights: ["Goonie Pay", "AutoBidders Integration", "Dealer Reputation Index", "Cross-Market AI"],
            status: "vision",
        },
    ];

    return (
        <section className="relative py-20 px-6 container mx-auto">
            <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-4xl font-bold text-center mb-12"
            >
                IDSCRM Roadmap
            </motion.h2>

            <div className="relative border-l border-muted pl-8 space-y-10 max-w-3xl mx-auto">
                {phases.map((p, idx) => (
                    <motion.div
                        key={p.title}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.15 }}
                        viewport={{ once: true }}
                        className="relative"
                    >
                        {/* Dot */}
                        <div
                            className={`absolute -left-[1.25rem] mt-1.5 h-4 w-4 rounded-full bg-gradient-to-br ${p.color} shadow-md`}
                        ></div>

                        <div className="bg-card border rounded-2xl p-6 shadow-md space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {p.icon}
                                    <h3 className="font-semibold text-lg">{p.title}</h3>
                                </div>
                                <span className="text-xs text-muted-foreground">{p.period}</span>
                            </div>

                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {p.description}
                            </p>

                            <div className="flex flex-wrap gap-2 pt-2">
                                {p.highlights.map((h) => (
                                    <span
                                        key={h}
                                        className="bg-muted text-xs px-2 py-1 rounded-full border border-border"
                                    >
                                        {h}
                                    </span>
                                ))}
                            </div>

                            <div className="pt-3">
                                {p.status === "complete" && (
                                    <div className="flex items-center gap-2 text-emerald-500 text-xs font-semibold">
                                        <CheckCircle2 className="h-4 w-4" /> Complete
                                    </div>
                                )}
                                {p.status === "active" && (
                                    <div className="flex items-center gap-2 text-sky-500 text-xs font-semibold">
                                        <Zap className="h-4 w-4" /> In Progress
                                    </div>
                                )}
                                {p.status === "upcoming" && (
                                    <div className="flex items-center gap-2 text-amber-500 text-xs font-semibold">
                                        <Layers className="h-4 w-4" /> Upcoming
                                    </div>
                                )}
                                {p.status === "vision" && (
                                    <div className="flex items-center gap-2 text-purple-500 text-xs font-semibold">
                                        <Brain className="h-4 w-4" /> Visionary
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </section>
    );
}
